import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const MAX_BARS = 10;

interface PPDynamicBarChartProps {
  title: string;
  chartData: ChartData<'bar'>;
  fullChartData?: ChartData<'bar'>;
  tooltipLabels?: string[];
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 0,
  },
  plugins: {
    legend: { display: true, position: 'top' as const },
  },
  scales: {
    x: { grid: { display: false }, offset: true },
    y: {
      beginAtZero: true,
      ticks: {
        padding: 0,
        callback: (value: number | string) => {
          const num = Number(value);
          if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
          return num;
        },
      },
    },
  },
};

const PPDynamicBarChart: React.FC<PPDynamicBarChartProps> = ({
  title,
  chartData,
  fullChartData,
  tooltipLabels,
}) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [viewMode, setViewMode] = useState<'chart' | 'grid'>('chart');

  const totalBars = chartData.labels?.length ?? 0;
  const hasMore = totalBars > MAX_BARS || !!fullChartData;

  // Chart options with custom tooltip when tooltipLabels provided
  const resolvedOptions = tooltipLabels
    ? {
        ...chartOptions,
        plugins: {
          ...chartOptions.plugins,
          tooltip: {
            callbacks: {
              title: (context: { dataIndex: number }[]) => {
                const idx = context[0]?.dataIndex ?? 0;
                return tooltipLabels[idx] ?? '';
              },
            },
          },
        },
      }
    : chartOptions;

  // Truncated data — show only first 10 bars
  const truncatedData: ChartData<'bar'> = hasMore
    ? {
        labels: chartData.labels?.slice(0, MAX_BARS),
        datasets: chartData.datasets.map((ds) => ({
          ...ds,
          data: ds.data.slice(0, MAX_BARS),
        })),
      }
    : chartData;

  // Build grid data from chart data
  const gridData = (chartData.labels ?? []).map((label, idx) => {
    const row: Record<string, unknown> = { sno: idx + 1, label };
    chartData.datasets.forEach((ds) => {
      const key = ds.label ?? `value_${idx}`;
      row[key] = ds.data[idx] ?? 0;
    });
    return row;
  });

  // Get dataset column names
  const datasetColumns = chartData.datasets.map(
    (ds, idx) => ds.label ?? `Value ${idx + 1}`
  );

  return (
    <>
      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">{title}</h6>
          <div className="d-flex gap-2">
            {/* Chart / Grid toggle */}
            <Button
              icon={viewMode === 'chart' ? 'pi pi-table' : 'pi pi-chart-bar'}
              tooltip={viewMode === 'chart' ? 'Grid View' : 'Chart View'}
              className="p-button-text p-button-sm"
              onClick={() =>
                setViewMode(viewMode === 'chart' ? 'grid' : 'chart')
              }
            />
            {/* View All (only in chart mode with >10 bars) */}
            {hasMore && viewMode === 'chart' && (
              <Button
                label="View All"
                icon="pi pi-external-link"
                className="p-button-text p-button-sm"
                onClick={() => setDialogVisible(true)}
              />
            )}
          </div>
        </div>

        {/* Chart View */}
        {viewMode === 'chart' && (
          <div style={{ height: '280px' }}>
            <Bar data={truncatedData} options={resolvedOptions} />
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div style={{ minHeight: '280px' }}>
            <DataTable
              value={gridData}
              paginator
              rows={5}
              stripedRows
              size="small"
              scrollable
              scrollHeight="250px"
              emptyMessage="No data available."
            >
              <Column
                header="S.No"
                body={(_row, { rowIndex }) => rowIndex + 1}
                style={{ width: '60px' }}
              />
              <Column field="label" header="Name" sortable />
              {datasetColumns.map((col) => (
                <Column
                  key={col}
                  field={col}
                  header={col}
                  sortable
                  body={(row) => Number(row[col] ?? 0).toLocaleString()}
                />
              ))}
            </DataTable>
          </div>
        )}
      </div>

      {/* Dialog with full chart */}
      <Dialog
        header={title}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '90vw' }}
        maximizable
      >
        <div style={{ height: '450px' }}>
          <Bar data={fullChartData ?? chartData} options={chartOptions} />
        </div>
      </Dialog>
    </>
  );
};

export default PPDynamicBarChart;
